import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Button, Card, Form, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaNetworkWired, 
  FaCheckCircle,
  FaLightbulb,
  FaRocket,
  FaFilter,
  FaSearch,
  FaTh,
  FaList,
  FaCog,
  FaServer,
  FaExternalLinkAlt,
  FaPhone,
  FaChevronLeft,
  FaChevronRight,
  FaEthernet,
  FaWifi,
  FaCloud
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const animationVariants = {
  fadeInUp: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8 }
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.6 }
  }
};

const CoreSwitching = () => {
  const { lang, t: trans } = useLanguage();
  const pageT = trans.coreSwitching;

  const SWITCH_DATA = useMemo(() => ({
    categories: [
      {
        id: 'catalyst-core-modular',
        name: lang === 'fr' ? 'Catalyst Core Modular' : 'Catalyst Core Modular',
        description: lang === 'fr' ? 'Châssis modulaires (9600/9600X)' : 'Modular chassis (9600/9600X)',
        icon: <FaNetworkWired />
      },
      {
        id: 'catalyst-core-fixed',
        name: lang === 'fr' ? 'Catalyst Core Fixed' : 'Catalyst Core Fixed',
        description: lang === 'fr' ? 'Switches fixes (9500/9500X)' : 'Fixed switches (9500/9500X)',
        icon: <FaRocket />
      },
      {
        id: 'distribution',
        name: lang === 'fr' ? 'Aggrégation' : 'Aggregation',
        description: lang === 'fr' ? 'Plateformes Catalyst 9400' : 'Catalyst 9400 platforms',
        icon: <FaServer />
      },
      {
        id: 'meraki-aggregation',
        name: lang === 'fr' ? 'Meraki Aggregation' : 'Meraki Aggregation',
        description: lang === 'fr' ? 'Cloud-managed (MS425/MS450)' : 'Cloud-managed (MS425/MS450)',
        icon: <FaCloud />
      }
    ],
    switches: [
        {
            id: 1,
            name: 'Cisco Catalyst 9600 Series',
            category: 'catalyst-core-modular',
            switchType: 'modular',
            isNew: false,
            image: '/images/CoreSwitch/cisco-cat9600.png',
            specs: {
              ports: lang === 'fr' ? "Jusqu'à 400G par port" : "Up to 400G per port",
              uplinks: '40/100/400G',
              backplane: 'Multi-Terabits fabric',
              rackUnits: lang === 'fr' ? 'Châssis' : 'Chassis'
            },
            workloads: ['Campus core', 'SD-Access fabric', 'Aggregation'],
            features: lang === 'fr' ? ['Redondance supervisors/PSU', 'MACsec', 'Programmabilité'] : ['Supervisors/PSU Redundancy', 'MACsec', 'Programmability'],
            badge: 'Core'
          },
          {
            id: 2,
            name: 'Cisco Catalyst 9600X Series',
            category: 'catalyst-core-modular',
            switchType: 'modular',
            isNew: true,
            image: '/images/CoreSwitch/cisco-cat9600x.png',
            specs: {
              ports: 'Line cards 100G/400G',
              uplinks: '100/400G',
              backplane: lang === 'fr' ? 'Fabric évolutive' : 'Scalable fabric',
              rackUnits: lang === 'fr' ? 'Châssis' : 'Chassis'
            },
            workloads: ['Core performance', 'EVPN/VXLAN', 'SD-Access'],
            features: lang === 'fr' ? ['400G ready', 'Haute résilience', 'QoS avancée'] : ['400G ready', 'High resilience', 'Advanced QoS'],
            badge: lang === 'fr' ? 'Nouveau' : 'New'
          },
          {
            id: 3,
            name: 'Cisco Catalyst 9500 Series',
            category: 'catalyst-core-fixed',
            switchType: 'fixed',
            isNew: false,
            image: '/images/CoreSwitch/cisco-cat9500.png',
            specs: {
              ports: '10/25/40/100G',
              uplinks: 'Up to 100G',
              backplane: 'Tbps performance',
              rackUnits: '1U'
            },
            workloads: ['Distribution', 'Core fixe', 'SD-Access border'],
            features: ['StackWise Virtual', 'MACsec', 'Automation'],
            badge: 'Distribution'
          },
          {
            id: 4,
            name: 'Cisco Catalyst 9500X Series',
            category: 'catalyst-core-fixed',
            switchType: 'fixed',
            isNew: true,
            image: '/images/CoreSwitch/cisco-cat9500x.png',
            specs: {
              ports: '25/40/100G',
              uplinks: '100G',
              backplane: 'High performance',
              rackUnits: '1U'
            },
            workloads: ['Core/Distribution', 'Uplinks 100G', 'EVPN'],
            features: ['MACsec 256-bit', 'Programmability', 'High availability'],
            badge: 'Performance'
          },
          {
            id: 6,
            name: 'Cisco Catalyst 9400 Series',
            category: 'distribution',
            switchType: 'modular',
            isNew: false,
            image: '/images/CoreSwitch/cisco-cat9400.png',
            specs: {
              ports: '1/10/25G per slot',
              uplinks: '40/100G',
              backplane: 'Fabric multi-Tbps',
              rackUnits: '7U/10U'
            },
            workloads: ['Distribution', 'Aggregation', 'High density'],
            features: ['Redundancy', 'DNA/Automation', 'Stack/SSO'],
            badge: 'Distribution'
          },
          {
            id: 7,
            name: 'Cisco Meraki MS450',
            category: 'meraki-aggregation',
            switchType: 'cloud-managed',
            isNew: false,
            image: '/images/CoreSwitch/cisco-meraki-ms450.png',
            specs: {
              ports: '32 x 40G QSFP+',
              uplinks: '8 x 100G',
              backplane: '2.56 Tbps',
              rackUnits: '1U'
            },
            workloads: ['Aggregation', 'Cloud-managed', 'Light Core'],
            features: ['Meraki Dashboard', 'Zero-touch', 'Analytics'],
            badge: 'Cloud'
          }
    ]
  }), [lang]);

  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);
  const [setSwitchesRef, switchesInView] = useScrollAnimation(0.1);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewMode === 'grid' ? 3 : 5;

  const filteredSwitches = useMemo(() => {
    let result = SWITCH_DATA.switches;
    if (selectedCategory !== 'all') result = result.filter(sw => sw.category === selectedCategory);
    if (searchTerm) {
      result = result.filter(sw =>
        sw.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sw.workloads.some(w => w.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    return result;
  }, [selectedCategory, searchTerm, SWITCH_DATA]);

  const totalPages = Math.ceil(filteredSwitches.length / itemsPerPage);
  const paginatedSwitches = filteredSwitches.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  return (
    <div>
      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{pageT.seo?.title || "Cisco Core Switching Tunisie | S2i"}</title>
        <meta name="description" content={pageT.seo?.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/core-switching" />
        
        {/* Open Graph pour LinkedIn */}
        <meta property="og:title" content={pageT.seo?.title} />
        <meta property="og:description" content={pageT.seo?.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/CoreSwitch/cisco-cat9600.png" />
      </Helmet>
      {/* ---------------------------------- */}


      {/* Hero Section */}
      <section ref={setHeroRef} className="hero-section-sii" style={{ background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/AccessSwitch/cisco-campus-switching1.jpg')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', minHeight: '80vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <Badge bg="danger" className="mb-3 px-3 py-2 rounded-pill">{pageT.hero.badge}</Badge>
                <h1 className="hero-title-sii text-white" dangerouslySetInnerHTML={{ __html: pageT.hero.title }} />
                <p className="hero-subtitle-sii text-white opacity-90">{pageT.hero.subtitle}</p>
                <div className="mt-4">
                  <Button variant="danger" className="btn-sii-glow btn-lg px-5 rounded-pill" onClick={() => document.getElementById('switches-grid').scrollIntoView({ behavior: 'smooth' })}>
                    {pageT.hero.btnExplore}
                  </Button>
                </div>
              </motion.div>
            </Col>
            <Col lg={6} className="text-center">
                <motion.img initial={{ opacity: 0, x: 50 }} animate={heroInView ? { opacity: 1, x: 0 } : {}} src="/images/CoreSwitch/campus-lan-switches.jpg" style={{ width: '100%', maxWidth: 500, borderRadius: 16, boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }} />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Categories Top Section (Style Cards) */}
      <section ref={setCategoriesRef} className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-2">{pageT.categoriesTitle}</h2>
            <p className="text-muted">{pageT.categoriesSubtitle}</p>
          </div>
          <Row className="g-4 justify-content-center">
            {SWITCH_DATA.categories.map((cat, idx) => (
              <Col key={cat.id} lg={3} md={6}>
                <motion.div variants={animationVariants.scaleIn} initial="initial" animate={categoriesInView ? "animate" : "initial"} transition={{ delay: idx * 0.1 }}>
                  <Card 
                    className={`h-100 text-center border-0 shadow-sm category-card ${selectedCategory === cat.id ? 'active' : ''}`}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease', border: selectedCategory === cat.id ? '2px solid #d90000' : 'none' }}
                    onClick={() => handleCategoryChange(cat.id)}
                  >
                    <Card.Body className="p-4">
                      <div className="mb-3" style={{ fontSize: '2.5rem', color: '#d90000' }}>{cat.icon}</div>
                      <h5 className="fw-bold mb-2">{cat.name}</h5>
                      <p className="text-muted small mb-0">{cat.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Main Grid & SIDEBAR (STYLE CORRIGÉ) */}
      <section id="switches-grid" className="py-5">
        <Container fluid>
          <Row>
            {/* Sidebar avec design original restauré */}
            <Col lg={3}>
              <div style={{ position: 'sticky', top: '100px', background: '#fff', borderRadius: '20px', padding: '2rem', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '2px solid #e9ecef' }}>
                  <FaFilter style={{ fontSize: '1.2rem', color: '#d90000', marginRight: '0.75rem' }} />
                  <h5 style={{ margin: 0, fontWeight: 700, color: '#1a202c' }}>{pageT.sidebar.title}</h5>
                </div>

                <div className="mb-4">
                  <label className="text-uppercase small fw-bold text-muted mb-2 d-block">{pageT.sidebar.searchLabel}</label>
                  <div className="position-relative">
                    <FaSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                    <Form.Control type="text" placeholder={pageT.sidebar.searchPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ borderRadius: '12px', paddingLeft: '2.5rem', background: '#f8f9fa', border: '2px solid #eee' }} />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-uppercase small fw-bold text-muted mb-2 d-block">{pageT.sidebar.catLabel}</label>
                  <div className="d-grid gap-2">
                    <Button variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange('all')} style={{ borderRadius: '12px', textAlign: 'left', padding: '0.75rem 1rem', border: selectedCategory === 'all' ? 'none' : '1px solid #dee2e6' }}>
                      <FaNetworkWired className="me-2" /> {pageT.sidebar.allSwitches}
                    </Button>
                    {SWITCH_DATA.categories.map(c => (
                      <Button key={c.id} variant={selectedCategory === c.id ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange(c.id)} style={{ borderRadius: '12px', textAlign: 'left', padding: '0.75rem 1rem', border: selectedCategory === c.id ? 'none' : '1px solid #dee2e6' }}>
                        <span className="me-2">{c.icon}</span> {c.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '12px', textAlign: 'center', marginBottom: '1.5rem' }}>
                    <p className="mb-0 small fw-bold"><span className="text-danger">{filteredSwitches.length}</span> {pageT.sidebar.found}</p>
                </div>

                <div>
                  <label className="text-uppercase small fw-bold text-muted mb-2 d-block">{pageT.sidebar.viewLabel}</label>
                  <div className="btn-group w-100" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <Button variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('grid')} style={{ border: 'none' }}><FaTh className="me-2"/>Grid</Button>
                    <Button variant={viewMode === 'list' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('list')} style={{ border: 'none' }}><FaList className="me-2"/>List</Button>
                  </div>
                </div>
              </div>
            </Col>

            {/* Main Content Area */}
            <Col lg={9}>
              <div ref={setSwitchesRef}>
                {viewMode === 'grid' ? (
                  <Row className="g-4">
                    {paginatedSwitches.map((sw, idx) => (
                      <Col md={6} lg={4} key={sw.id}>
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={switchesInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: idx * 0.1 }}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden solution-card">
                            <div className="p-4 bg-light text-center position-relative" style={{ height: '200px' }}>
                                {sw.isNew && <Badge bg="success" className="position-absolute top-0 start-0 m-3">{pageT.card.new}</Badge>}
                                <Badge bg="danger" className="position-absolute top-0 end-0 m-3">{sw.badge}</Badge>
                                <img src={sw.image} alt={sw.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            <Card.Body className="p-4">
                              <h5 className="fw-bold mb-3">{sw.name}</h5>
                              <div className="mb-3 d-flex flex-wrap gap-2">
                                {sw.workloads.map((w, i) => <Badge key={i} bg="light" text="dark" className="border">{w}</Badge>)}
                              </div>
                              <div className="small text-muted mb-3 border-bottom pb-3">
                                <div className="d-flex justify-content-between mb-1"><span>{pageT.card.ports}:</span> <span className="text-dark fw-bold">{sw.specs.ports}</span></div>
                                <div className="d-flex justify-content-between"><span>Fabric:</span> <span className="text-dark fw-bold">{sw.specs.backplane}</span></div>
                              </div>
                              <label className="small fw-bold text-uppercase text-muted mb-2 d-block">{pageT.card.points}</label>
                              {sw.features.map((f, i) => <div key={i} className="small mb-1"><FaCheckCircle className="text-success me-2"/>{f}</div>)}
                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {paginatedSwitches.map((sw) => (
                      <Card key={sw.id} className="border-0 shadow-sm rounded-4 overflow-hidden p-3">
                        <Row className="align-items-center">
                          <Col md={2}><img src={sw.image} style={{ width: '100%', maxHeight: '80px', objectFit: 'contain' }} /></Col>
                          <Col md={10}>
                            <h5 className="fw-bold mb-1">{sw.name} <Badge bg="danger" className="ms-2">{sw.badge}</Badge></h5>
                            <p className="small text-muted mb-2">{sw.workloads.join(' • ')}</p>
                            <div className="small">{sw.features.map(f => <span key={f} className="me-3"><FaCheckCircle className="text-success me-1"/>{f}</span>)}</div>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center gap-2 mt-5">
                    <Button variant="outline-danger" className="rounded-circle" onClick={() => setCurrentPage(prev => Math.max(prev-1, 1))}><FaChevronLeft/></Button>
                    {[...Array(totalPages)].map((_, i) => (
                        <Button key={i} variant={currentPage === i+1 ? 'danger' : 'outline-secondary'} className="rounded-circle" onClick={() => setCurrentPage(i+1)}>{i+1}</Button>
                    ))}
                    <Button variant="outline-danger" className="rounded-circle" onClick={() => setCurrentPage(prev => Math.min(prev+1, totalPages))}><FaChevronRight/></Button>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Final */}
      <section className="py-5 bg-white">
        <Container>
            <motion.div className="bg-dark p-5 rounded-5 text-white text-center shadow-lg" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
                <FaLightbulb className="text-danger fs-1 mb-4" />
                <h2 className="fw-bold mb-3">{pageT.cta.title}</h2>
                <p className="lead mb-4 opacity-75">{pageT.cta.subtitle}</p>
                <div className="d-flex justify-content-center gap-3">
                    <Button variant="danger" size="lg" className="px-5 rounded-pill" onClick={() => window.location.href='/#contact'}>{pageT.cta.btnExpert}</Button>
                    <Button variant="outline-light" size="lg" className="px-5 rounded-pill">{pageT.cta.btnCatalog}</Button>
                </div>
            </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default CoreSwitching;